import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import classnames from 'classnames';
import ClickableElement from '../ClickableElement';
import {
  RadioStarSelect,
  RadioHeartSelect,
  RadioSquareSelect,
  RadioCircleSelect,
  RadioStarUnselect,
  RadioHeartUnselect,
  RadioCircleUnselect,
  RadioSquareUnselect,
  RadioOutlinedCircleSelect,
  RadioOutlinedCircleUnselect,
} from '@/svgs';
import { colors, semiBold, lightInter } from '@/styles';

/**
 * @typedef {import('./RadioButton').RadioButtonProps} RadioButtonProps
 */

/**
 * @param {RadioButtonProps['type']} type
 * @param {unknown} size
 */
const mapSvgs = (type, size) => {
  switch (type) {
    case 'heart':
      return [RadioHeartSelect, RadioHeartUnselect];
    case 'square':
      return [RadioSquareSelect, RadioSquareUnselect];
    case 'star':
      return [RadioStarSelect, RadioStarUnselect];
    case 'outlinedCircle':
      return [RadioOutlinedCircleSelect, RadioOutlinedCircleUnselect];
    case 'circle':
    default:
      return [RadioCircleSelect, RadioCircleUnselect];
  }
};

/**
 * RadioButton component
 * @param {RadioButtonProps} props
 */
const RadioButton = ({
  className,
  disabled,
  onChange,
  role,
  selected,
  selectedColor,
  size,
  title,
  type,
  unselectedColor,
  children,
  id,
}) => {
  const [active, setActive] = useState(selected);
  const [Select, Unselect] = mapSvgs(type, size);

  useEffect(() => {
    if (!disabled || !selected) {
      setActive(selected);
    }
  }, [disabled, selected]);

  const handleClick = (event) => {
    event.preventDefault();
    if (disabled) {
      return;
    }
    setActive(!active);
    onChange(event);
  };

  return (
    <ClickableElement
      role={role}
      id={id}
      onClick={handleClick}
      className={className}
      aria-checked={active}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
    >
      {active ? (
        <Select title={title} size={size} fill={disabled ? colors.silverSand : selectedColor} />
      ) : (
        <Unselect title={title} size={size} fill={disabled ? colors.silverSand : unselectedColor} />
      )}
      <div className={classnames('children', { selected: active })}>{children}</div>
    </ClickableElement>
  );
};

RadioButton.propTypes = {
  /** Emotion class name */
  className: PropTypes.string.isRequired,
  /** The type of radio button */
  type: PropTypes.oneOf(['circle', 'heart', 'square', 'star', 'outlinedCircle']),
  /** Wheather the radio button is currently selected */
  selected: PropTypes.bool,
  /** What to do when the button is clicked. */
  onChange: PropTypes.func,
  /** Selected color of the radio button */
  selectedColor: PropTypes.string,
  /** The size passed to the icons */
  size: PropTypes.string,
  /** Can the button be used */
  disabled: PropTypes.bool,
  /** Unselected color of the radio button */
  unselectedColor: PropTypes.string,
  /** ARIA role */
  role: PropTypes.string,
  /** Title for the SVG */
  title: PropTypes.string,
  /** Content to render within the button */
  children: PropTypes.node,
  /** ID to give the button, can tie this to any labels within children */
  id: PropTypes.string,
};

RadioButton.defaultProps = {
  type: 'square',
  role: 'button',
  selected: false,
  selectedColor: colors.silverSandAlt,
  size: '10',
  disabled: false,
  onChange: () => {},
  unselectedColor: colors.silverSandAlt,
  title: '',
  children: null,
  id: null,
};

const styledRadioButton = styled(RadioButton)`
  outline: none;
  display: flex;

  ${(props) => {
    if (props.disabled) {
      return `
        cursor: default;
        pointer-events: none;
      `;
    }
    return 'cursor: pointer;';
  }}

  svg {
    transition: 0.1s;
    flex-shrink: 0;
    margin-top: 0.125rem;
  }

  &:focus {
    svg {
      ${(props) => {
        if (props.type === 'outlinedCircle') {
          return `
            outline: ${colors.roberto} solid 0.063rem;
            border-radius: 4rem;
            outline-offset: 0.063rem;
          `;
        }

        return `
          filter: drop-shadow(0 0 0.0625rem ${colors.activeShadow})
                  drop-shadow(0 0 0.0625rem ${colors.activeShadow});
        `;
      }}
    }
  }

  .children {
    ${lightInter}
    color: ${colors.boulder};
    font-size: 0.75rem;

    margin-left: 0.625rem;

    label {
      ${semiBold()}
      font-size: 0.75rem;
      color: ${colors.shadow};
      margin-bottom: 0.125rem;
      margin-right: 0.5rem;
      display: inline-block;
    }

    &.selected label {
      color: ${colors.electricBlue};
    }
  }
`;

export default styledRadioButton;
